import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Clock, AlertTriangle, Search, FileDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { usePdfExport } from '@/hooks/usePdfExport';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AGEING_THRESHOLDS = [
  { label: '> 7 days', days: 7, color: 'text-red-600', bg: 'bg-red-50 border-red-200' },
  { label: '4-7 days', days: 4, color: 'text-orange-600', bg: 'bg-orange-50 border-orange-200' },
  { label: '1-3 days', days: 1, color: 'text-yellow-700', bg: 'bg-yellow-50 border-yellow-200' },
  { label: '< 1 day', days: 0, color: 'text-green-600', bg: 'bg-green-50 border-green-200' },
];

function getAgeingLevel(days) {
  if (days >= 7) return AGEING_THRESHOLDS[0];
  if (days >= 4) return AGEING_THRESHOLDS[1];
  if (days >= 1) return AGEING_THRESHOLDS[2];
  return AGEING_THRESHOLDS[3];
}

export default function StageAgeing() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const { exporting, handleExport } = usePdfExport('/export/stage-ageing-pdf', 'Stage_Ageing');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/reports/stage-ageing`, {
        
      });
      setData(response.data);
    } catch (err) { console.error(err);
      toast.error('Failed to fetch stage ageing data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filtered = data.filter(d =>
    !search || d.part_id.toLowerCase().includes(search.toLowerCase()) ||
    (d.part_name || '').toLowerCase().includes(search.toLowerCase()) ||
    d.stage.toLowerCase().includes(search.toLowerCase())
  );

  const summaryByStage = {};
  data.forEach(d => {
    if (!summaryByStage[d.stage]) summaryByStage[d.stage] = { totalQty: 0, maxDays: 0, count: 0 };
    summaryByStage[d.stage].totalQty += d.qty;
    summaryByStage[d.stage].count += 1;
    if (d.days_in_stage > summaryByStage[d.stage].maxDays) summaryByStage[d.stage].maxDays = d.days_in_stage;
  });

  const criticalCount = data.filter(d => d.days_in_stage >= 7).length;

  return (
    <div className="space-y-6" data-testid="stage-ageing-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold tracking-tight uppercase" style={{ fontFamily: 'Barlow Condensed, sans-serif' }} data-testid="stage-ageing-title">STAGE AGEING</h1>
          <p className="text-sm text-text-muted mt-2">Identify bottlenecks by tracking how long parts sit in each production stage</p>
        </div>
        {data.length > 0 && (
          <Button data-testid="export-pdf-button" onClick={() => handleExport()} disabled={exporting} className="bg-green-600 text-white hover:bg-green-700 rounded-sm font-medium">
            <FileDown className="w-4 h-4 mr-2" />{exporting ? 'EXPORTING...' : 'EXPORT PDF'}
          </Button>
        )}
      </div>

      {data.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {Object.entries(summaryByStage).map(([stage, info]) => {
            const level = getAgeingLevel(info.maxDays);
            return (
              <div key={stage} className={`border rounded-sm p-3 ${level.bg}`} data-testid={`stage-card-${stage}`}>
                <p className="text-xs font-medium uppercase tracking-wider text-text-muted">{stage}</p>
                <p className="text-lg font-bold font-mono mt-1">{info.totalQty.toLocaleString()} pcs</p>
                <p className={`text-xs font-mono ${level.color} mt-0.5`}>{info.count} parts, max {info.maxDays}d</p>
              </div>
            );
          })}
        </div>
      )}

      {criticalCount > 0 && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-sm p-3" data-testid="critical-alert">
          <AlertTriangle className="w-4 h-4 text-red-600 shrink-0" />
          <p className="text-sm text-red-700 font-medium">{criticalCount} part(s) have been in the same stage for 7+ days</p>
        </div>
      )}

      <div className="bg-white border border-border rounded-sm">
        <div className="p-4 border-b border-border flex items-center justify-between gap-4 flex-wrap">
          <h2 className="text-sm font-semibold uppercase tracking-wider">Ageing Details</h2>
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
            <Input data-testid="search-input" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search part, stage..." className="pl-9 rounded-sm" />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full caption-bottom text-sm" data-testid="ageing-table">
            <thead>
              <tr className="border-b">
                <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Part ID</th>
                <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Part Name</th>
                <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Stage</th>
                <th className="h-10 px-4 text-right align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Qty</th>
                <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Last Activity</th>
                <th className="h-10 px-4 text-right align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Days in Stage</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="p-8 text-center text-text-muted">Loading...</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={6} className="p-8 text-center text-text-muted">No parts currently in any stage</td></tr>
              ) : filtered.map((row, idx) => {
                const level = getAgeingLevel(row.days_in_stage);
                return (
                  <tr key={idx} className="border-b transition-colors hover:bg-muted/50" data-testid={`ageing-row-${idx}`}>
                    <td className="p-4 align-middle font-mono text-xs font-semibold">{row.part_id}</td>
                    <td className="p-4 align-middle text-xs text-text-muted">{row.part_name || '—'}</td>
                    <td className="p-4 align-middle">
                      <span className="inline-flex items-center rounded-full bg-blue-50 border border-blue-200 px-2 py-0.5 text-xs text-blue-700 font-medium">{row.stage}</span>
                    </td>
                    <td className="p-4 align-middle text-right font-mono text-xs">{row.qty.toLocaleString()}</td>
                    <td className="p-4 align-middle text-xs text-text-muted font-mono">
                      {row.last_activity ? new Date(row.last_activity).toLocaleDateString() : '—'}
                    </td>
                    <td className="p-4 align-middle text-right">
                      <span className={`inline-flex items-center gap-1 font-mono text-xs font-bold ${level.color}`}>
                        <Clock className="w-3 h-3" />
                        {row.days_in_stage}d
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
